import React from "react";
import { useLocalization } from "@fluent/react";
import App from "byzantine/src/App";
import { translateOAuthApplication } from "byzantine/src/l10n/SettingLocalizers";
import PropTypes from "prop-types";
import { Button, Dialog } from "@narmi/design_system";
import { useAppLaunch } from "../src/modules/appAuthorize";

/**
 * Default button rendering for legacy components that use
 * `children` to define the button connected to the authorize Dialog
 */
export const defaultRenderButton = (
  handleClick,
  children,
  className,
  dataClassnames,
  tabIndex,
  role
) => (
  <button
    type="button"
    className={`button--reset topMenu-link ${className}`}
    data-classname={
      dataClassnames ? `AppAuthorize ${dataClassnames}` : "AppAuthorize"
    }
    onClick={handleClick}
    tabIndex={tabIndex}
    role={role}
  >
    {children}
  </button>
);

const AppAuthorize = ({
  appId = /** @type {any} */ (undefined),
  institution = /** @type {any} */ (undefined),
  app,
  children = /** @type {any} */ (undefined),
  autoLaunch = /** @type {any} */ (undefined),
  className = /** @type {any} */ (undefined),
  dataClassnames = /** @type {any} */ (undefined),
  renderButton = defaultRenderButton,
}) => {
  const { l10n } = useLocalization();
  const currentApp =
    app || App.deserialize(institution.apps.find((a) => a.client_id === appId));

  const {
    handleClick,
    handleAuthorize,
    onUserDismiss,
    isLoading,
    promptAuthorize,
  } = useAppLaunch(currentApp, autoLaunch);

  // Localize the app data - the function handles all checking internally
  const localizedApp = translateOAuthApplication(
    {
      uuid: currentApp.id,
      name: currentApp.name,
      description: currentApp.description,
    },
    l10n
  );

  if (!isLoading) {
    return (
      <>
        {renderButton(handleClick, children, className, dataClassnames)}
        <Dialog
          isOpen={promptAuthorize}
          onUserDismiss={onUserDismiss}
          title={l10n.getString("dialog-title-app-permission", {
            appName: localizedApp.name,
          })}
          footer={
            <>
              <Button
                kind="negative"
                onClick={onUserDismiss}
                style={{ paddingRight: "16px" }}
                label={l10n.getString("button-cancel")}
              />
              <Button
                onClick={handleAuthorize}
                label={l10n.getString("button-proceed")}
              />
            </>
          }
        >
          {l10n.getString("app-permission-disclosure", {
            appName: localizedApp.name,
          })}
          <ul>
            {Object.values(currentApp.scopes)
              .filter((x) => x)
              .map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
          </ul>
        </Dialog>
      </>
    );
  }
  return null;
};

AppAuthorize.propTypes = {
  appId: PropTypes.string,
  institution: PropTypes.object,
  app: PropTypes.object,
  autoLaunch: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  dataClassnames: PropTypes.string,
  /**
   * Render prop called with click handler
   * renderButton: (handleClick: (e: HTMLDomEvent) => void) => void
   */
  renderButton: PropTypes.func,
};

export default AppAuthorize;
